// frontend/src/components/VendorSummaryTab.jsx
// NEW FILE — drop in frontend/src/components/

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton,
  Collapse, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Button, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, Tooltip, Divider, Avatar,
  TextField, InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import api from '../services/api';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Vendor color based on name (consistent colors)
const getVendorColor = (name) => {
  const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00796b', '#5d4037', '#455a64'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const InvoiceRow = ({ invoice, getFileUrl }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {invoice.billNumber || 'No Bill #'}
          </Typography>
        </TableCell>
        {invoice.siteName && (
          <TableCell>
            <Chip label={invoice.siteName} size="small" variant="outlined" />
          </TableCell>
        )}
        <TableCell>{formatDate(invoice.date)}</TableCell>
        <TableCell align="center">
          <Chip label={`${invoice.itemCount} item${invoice.itemCount !== 1 ? 's' : ''}`} size="small" color="info" variant="outlined" />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600} color="success.main">
            {formatCurrency(invoice.invoiceTotal)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {invoice.billFileUrl ? (
            <Tooltip title="View Bill">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(getFileUrl(invoice.billFileUrl), '_blank');
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.disabled">No bill</Typography>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded: items in this invoice */}
      <TableRow>
        <TableCell colSpan={invoice.siteName ? 7 : 6} sx={{ p: 0, border: 0 }}>
          <Collapse in={expanded}>
            <Box sx={{ bgcolor: 'background.paper', px: 4, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                ITEMS IN THIS INVOICE
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Material</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>GST</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoice.items || []).map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{item.material || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{item.quantity} {item.unit || ''}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{item.gstPercentage != null ? `${item.gstPercentage}%` : '-'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {formatCurrency(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const VendorCard = ({ vendor, showSite, getFileUrl }) => {
  const [expanded, setExpanded] = useState(false);
  const color = getVendorColor(vendor.supplierName);
  const initial = vendor.supplierName.charAt(0).toUpperCase();

  return (
    <Card
      elevation={2}
      sx={{
        mb: 2,
        borderLeft: `4px solid ${color}`,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 6 }
      }}
    >
      <CardContent sx={{ pb: expanded ? 0 : '16px !important' }}>
        {/* Vendor Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 44, height: 44, fontSize: '1.1rem', fontWeight: 700 }}>
            {initial}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {vendor.supplierName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last purchase: {formatDate(vendor.lastPurchaseDate)}
            </Typography>
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {formatCurrency(vendor.totalSpent)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Spent</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {vendor.invoiceCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">Invoice{vendor.invoiceCount !== 1 ? 's' : ''}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="info.main">
                {vendor.totalItems}
              </Typography>
              <Typography variant="caption" color="text.secondary">Items</Typography>
            </Box>

            <IconButton onClick={() => setExpanded(!expanded)} sx={{ ml: 1 }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Expanded Invoices */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell width={40} />
                    <TableCell sx={{ fontWeight: 600 }}>Invoice / Bill #</TableCell>
                    {showSite && <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Bill</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendor.invoices.map((invoice, idx) => (
                    <InvoiceRow
                      key={idx}
                      invoice={invoice}
                      showSite={showSite}
                      getFileUrl={getFileUrl}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const VendorSummaryTab = ({ selectedSiteId, sites }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteMode, setSiteMode] = useState('site'); // 'site' | 'all'
  const [searchQuery, setSearchQuery] = useState('');

  const getFileUrl = (fileUrl) => {
    if (!fileUrl) return null;
    if (fileUrl.startsWith('http')) return fileUrl;
    return `${process.env.REACT_APP_API_URL || ''}${fileUrl}`;
  };

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let url = '/materials/vendor-summary';
      if (siteMode === 'all') {
        url += '?allSites=true';
      } else if (selectedSiteId) {
        url += `?siteId=${selectedSiteId}`;
      } else {
        setVendors([]);
        setLoading(false);
        return;
      }
      const res = await api.get(url);
      setVendors(res.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load vendor summary');
    } finally {
      setLoading(false);
    }
  }, [selectedSiteId, siteMode]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleDownloadReport = () => {
    if (!vendors.length) return;

    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Summary ──────────────────────────────────────
    const summaryRows = [
      ['Vendor Summary Report'],
      ['Generated on:', new Date().toLocaleString('en-IN')],
      ['Scope:', siteMode === 'all' ? 'All Sites' : sites.find(s => s.id === selectedSiteId)?.siteName || selectedSiteId],
      [],
      ['Vendor / Supplier', 'Total Invoices', 'Total Items', 'Total Amount (₹)', 'Last Purchase Date']
    ];
    vendors.forEach(v => {
      summaryRows.push([
        v.supplierName,
        v.invoiceCount,
        v.totalItems,
        v.totalSpent,
        formatDate(v.lastPurchaseDate)
      ]);
    });
    // Grand total row
    summaryRows.push([]);
    summaryRows.push([
      'GRAND TOTAL',
      vendors.reduce((s, v) => s + v.invoiceCount, 0),
      vendors.reduce((s, v) => s + v.totalItems, 0),
      vendors.reduce((s, v) => s + v.totalSpent, 0),
      ''
    ]);

    const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
    ws1['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

    // ── Sheet 2: Detailed ─────────────────────────────────────
    const detailRows = [
      ['Vendor', 'Bill Number', 'Site', 'Date', 'Material', 'Quantity', 'Unit', 'GST %', 'Amount (₹)']
    ];
    vendors.forEach(v => {
      v.invoices.forEach(inv => {
        (inv.items || []).forEach(item => {
          detailRows.push([
            v.supplierName,
            inv.billNumber || '',
            inv.siteName || '',
            formatDate(inv.date),
            item.material || '',
            item.quantity,
            item.unit || '',
            item.gstPercentage != null ? item.gstPercentage : '',
            item.amount || 0
          ]);
        });
      });
    });

    const ws2 = XLSX.utils.aoa_to_sheet(detailRows);
    ws2['!cols'] = [
      { wch: 25 }, { wch: 18 }, { wch: 25 }, { wch: 14 },
      { wch: 28 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(wb, ws2, 'Detailed');

    const siteName = siteMode === 'all' ? 'AllSites' : (sites.find(s => s.id === selectedSiteId)?.siteName || 'Site');
    XLSX.writeFile(wb, `VendorReport_${siteName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const showSite = siteMode === 'all';
  const filteredVendors = vendors.filter(v => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      v.supplierName?.toLowerCase().includes(q) ||
      v.invoices?.some(inv =>
        inv.billNumber?.toLowerCase().includes(q) ||
        inv.siteName?.toLowerCase().includes(q) ||
        inv.items?.some(item => item.material?.toLowerCase().includes(q))
      )
    );
  });
  const totalSpend = filteredVendors.reduce((s, v) => s + v.totalSpent, 0);

  return (
    <Box sx={{ p: 0 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={siteMode}
            exclusive
            onChange={(e, val) => { if (val) setSiteMode(val); }}
            size="small"
          >
            <ToggleButton value="site">This Site</ToggleButton>
            <ToggleButton value="all">All Sites</ToggleButton>
          </ToggleButtonGroup>

          {!loading && filteredVendors.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''} •{' '}
              <strong>{formatCurrency(totalSpend)}</strong> total
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search vendor, bill, material..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
            disabled={!vendors.length || loading}
            size="small"
          >
            Download Report
          </Button>
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* No site selected */}
      {!selectedSiteId && siteMode === 'site' && (
        <Alert severity="info">Please select a site to view vendor summary.</Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loading && !error && vendors.length === 0 && (selectedSiteId || siteMode === 'all') && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <BusinessIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No vendor data found</Typography>
          <Typography variant="body2" color="text.disabled">
            Material entries with supplier names will appear here
          </Typography>
        </Box>
      )}

      {/* Vendor Cards */}
      {!loading && filteredVendors.map((vendor) => (
        <VendorCard
          key={vendor.supplierName}
          vendor={vendor}
          showSite={showSite}
          getFileUrl={getFileUrl}
        />
      ))}
    </Box>
  );
};

export default VendorSummaryTab;
